import { useEffect, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import api, { rows, pageMeta } from "@/lib/api";

/**
 * Lista com paginação numerada (telas do painel).
 *
 * Os filtros vão para o servidor — sem isso a busca só enxergaria a página atual.
 * `search` entra com debounce e qualquer mudança de filtro volta para a página 1.
 *
 *   const { items, meta, page, setPage, search, setSearch, isLoading, refetch } =
 *     usePaginatedList("/admin/products", { category, status });
 */
export default function usePaginatedList(path, filters = {}, { perPage = 25, debounceMs = 300, sort: sortInicial = null } = {}) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  /* Ordenação mora aqui, junto de página e busca, e não em cada tela.
     São o mesmo tipo de estado: vão para o servidor, entram na chave do cache e
     precisam devolver o usuário à página 1 — ordenar por preço estando na página
     7 mostraria a sétima fatia de uma lista que acabou de mudar de ordem.
     `null` = ordem padrão do endpoint. */
  const [sort, setSort] = useState(sortInicial);

  useEffect(() => {
    const t = setTimeout(() => setTerm(search.trim()), debounceMs);
    return () => clearTimeout(t);
  }, [search, debounceMs]);

  // Filtro novo com o usuário na página 7 traria uma página vazia — volta para a 1.
  const filterKey = JSON.stringify(filters);
  const sortKey = sort ? `${sort.key}:${sort.dir}` : "";
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    setPage(1);
  }, [filterKey, term, sortKey]);

  const query = useQuery({
    queryKey: [path, filterKey, term, sortKey, page, perPage],
    // Mantém a página anterior na tela enquanto a próxima carrega (sem "piscar").
    placeholderData: keepPreviousData,
    queryFn: async () => {
      // `term` (busca interna do hook) vence; se a tela preferir controlar a
      // busca por fora, basta mandá-la dentro de `filters`.
      const res = await api.get(path, {
        params: {
          ...filters,
          ...(term ? { search: term } : {}),
          ...(sort ? { sort: sort.key, dir: sort.dir } : {}),
          page,
          per_page: perPage,
        },
      });
      return { items: rows(res), meta: pageMeta(res) };
    },
  });

  const meta = query.data?.meta ?? null;
  const lastPage = meta?.last_page ?? 1;

  return {
    ...query,
    items: query.data?.items ?? [],
    meta,
    total: meta?.total ?? (query.data?.items?.length ?? 0),
    page,
    lastPage,
    setPage: (p) => setPage(Math.min(Math.max(1, p), lastPage)),
    search,
    setSearch,
    // Vão direto para as props `sort`/`onSortChange` do DataTable.
    sort,
    setSort,
  };
}
